import { Row } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { Icons } from '../../Icons';
import {
  EXPANDER_MIN_WIDTH,
  EXPANDER_MIN_WIDTH_WITH_SELECTION,
} from '../constants';
import { ColumnGroup } from '../types';

export function useExpandColumn<TData>({
  getRowId,
  isRowExpandable,
  initiallyExpandedIds,
  expandedIds: controlledExpandedIds,
  onExpandedChange,
  sticky,
  width,
  ariaLabelToggle,
  enabled = true,
  hasRowSelection = false,
}: {
  getRowId: (row: TData) => string;
  isRowExpandable?: (row: TData) => boolean;
  initiallyExpandedIds?: string[];
  expandedIds?: string[];
  onExpandedChange?: (ids: string[]) => void;
  sticky?: 'left' | 'right';
  width?: number;
  ariaLabelToggle?: string;
  enabled?: boolean;
  hasRowSelection?: boolean;
}): {
  column: ColumnGroup<TData, unknown> | null;
  expandedIdSet: Set<string>;
  toggleExpandById: (id: string) => void;
  setExpandedIds: (ids: string[]) => void;
} {
  const isControlled = Array.isArray(controlledExpandedIds);
  const [uncontrolledExpandedIds, setUncontrolledExpandedIds] = useState<
    Set<string>
  >(new Set(initiallyExpandedIds || []));

  useEffect(() => {
    if (isControlled) return;
    setUncontrolledExpandedIds(new Set(initiallyExpandedIds || []));
  }, [initiallyExpandedIds?.join('|')]);

  const expandedIdSet = isControlled
    ? new Set(controlledExpandedIds)
    : uncontrolledExpandedIds;

  const setExpandedIds = (ids: string[]) => {
    if (isControlled) {
      onExpandedChange?.(ids);
    } else {
      setUncontrolledExpandedIds(new Set(ids));
      onExpandedChange?.(ids);
    }
  };

  const toggleExpandById = (id: string) => {
    const next = new Set(expandedIdSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedIds(Array.from(next));
  };

  if (!enabled) {
    return {
      column: null,
      expandedIdSet: new Set<string>(),
      toggleExpandById: () => {},
      setExpandedIds: () => {},
    };
  }

  const column: ColumnGroup<TData, unknown> = {
    id: 'expander',
    sticky: sticky || 'left',
    columns: [
      {
        id: 'expand',
        accessorKey: 'expand',
        header: () => null,
        cell: ({ row }: { row: Row<TData> }) => {
          const rowId = getRowId(row.original);
          const canExpand = isRowExpandable
            ? isRowExpandable(row.original)
            : true;
          const isExpanded = expandedIdSet.has(rowId);
          return (
            <button
              type="button"
              aria-label={ariaLabelToggle || 'Toggle row expansion'}
              disabled={!canExpand}
              onClick={(e) => {
                e.stopPropagation();
                if (!canExpand) return;
                toggleExpandById(rowId);
              }}
              className="expand-cell inline-flex shrink-0 items-center justify-center w-full min-w-4"
            >
              <Icons.ChevronRight
                className={`size-4 min-h-4 min-w-4 shrink-0 transition-transform duration-200 ${
                  isExpanded ? 'rotate-90' : ''
                } ${canExpand ? '' : 'opacity-40'}`}
              />
            </button>
          );
        },
        width: Math.max(
          width ?? 0,
          hasRowSelection
            ? EXPANDER_MIN_WIDTH_WITH_SELECTION
            : EXPANDER_MIN_WIDTH
        ),
        enableSorting: false,
        showInSelector: false,
      },
    ],
  };

  return { column, expandedIdSet, toggleExpandById, setExpandedIds };
}
